import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  X,
  SlidersHorizontal,
  Clock,
  Newspaper,
  Calendar,
  AlertCircle
} from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import AdBanner from '../components/AdBanner';
import '../styles/editorial-list.css';

const Blog = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [availableCategories, setAvailableCategories] = useState(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [lastRead, setLastRead] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  // Load live blog feed from backend API
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getArticleFeed(
          selectedCategory === 'all' ? null : selectedCategory,
          currentPage,
          50
        );
        const list = data || [];
        setPosts(list);

        // Dynamically extract categories present in database
        const catSet = new Set(list.map(item => item.category).filter(Boolean));
        setAvailableCategories(['all', ...Array.from(catSet)]);
      } catch (err) {
        console.error('Error loading live blog feed:', err);
        setError(err.message || 'Failed to load blog insights from server.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, [selectedCategory, currentPage]);

  // Load last read post session from localStorage
  useEffect(() => {
    if (isAuthenticated()) {
      const stored = localStorage.getItem('last_read_blog');
      if (stored) {
        try {
          setLastRead(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing last read blog:', e);
        }
      }
    } else {
      setLastRead(null);
    }
  }, [isAuthenticated]);

  const openBlogPost = (post) => {
    localStorage.setItem('last_read_blog', JSON.stringify({
      postId: post.id,
      title: post.title,
      author: post.authorName || post.author,
      slug: post.slug
    }));
    navigate(`/articles/${post.slug || post.id}`);
  };

  // Helper for category badge styling
  const getCatClass = (category) => {
    if (!category) return 'cat-default';
    const c = category.toLowerCase();
    if (c.includes('shalya') || c.includes('surg')) return 'cat-shalya';
    if (c.includes('case')) return 'cat-case';
    if (c.includes('ayur')) return 'cat-ayurveda';
    return 'cat-default';
  };

  // Filter & Search Logic
  const filteredPosts = posts.filter(post => {
    const query = searchTerm.toLowerCase().trim();
    const matchesSearch = !query || 
      (post.title && post.title.toLowerCase().includes(query)) ||
      (post.authorName && post.authorName.toLowerCase().includes(query)) ||
      (post.summary && post.summary.toLowerCase().includes(query)) ||
      (post.subtitle && post.subtitle.toLowerCase().includes(query));
    return matchesSearch;
  });

  // Sorting Logic
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (sortBy === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    } else if (sortBy === 'views') {
      return (b.viewCount || b.views || 0) - (a.viewCount || a.views || 0);
    } else if (sortBy === 'date') {
      const dateA = a.publishedAt ? new Date(a.publishedAt) : new Date(a.publishDate || 0);
      const dateB = b.publishedAt ? new Date(b.publishedAt) : new Date(b.publishDate || 0);
      return dateB - dateA;
    }
    return 0;
  });

  // Pagination Math
  const totalPages = Math.ceil(sortedPosts.length / postsPerPage) || 1;
  const indexOfLast = currentPage * postsPerPage;
  const indexOfFirst = indexOfLast - postsPerPage;
  const currentPosts = sortedPosts.slice(indexOfFirst, indexOfLast);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (num) => {
    setCurrentPage(num);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="editorial-page-container">
      
      {/* Top Menu-Adjacent Search & Category Control Bar */}
      <div className="top-menu-control-bar">
        <div className="container">
          <div className="top-control-bar-inner">
            
            {/* Search Input */}
            <div className="control-search-box">
              <Search className="control-search-icon" size={17} />
              <input
                type="text"
                placeholder="Search blog posts, topics, authors..."
                className="control-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="control-search-clear"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Category Pills */}
            <div className="control-pills-group">
              {availableCategories.map((cat) => (
                <button
                  key={cat}
                  className={`control-pill ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setCurrentPage(1);
                  }}
                >
                  {cat === 'all' ? 'All Blog Posts' : cat}
                </button>
              ))}
            </div>

            {/* Sort Select */}
            <div className="control-sort-box">
              <SlidersHorizontal size={15} color="#94a3b8" />
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="control-sort-select"
              >
                <option value="date">Sort: Latest Published</option>
                <option value="views">Sort: Most Views</option>
                <option value="title">Sort: Title A-Z</option>
              </select>
            </div>

          </div>
        </div>
      </div>

      {/* Main 2-Column Editorial Container */}
      <div className="container">
        
        {/* Resume Reading Quick Widget (if available) */}
        {isAuthenticated() && lastRead && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
            border: '1px solid rgba(0, 229, 255, 0.25)',
            borderRadius: '14px',
            padding: '12px 20px',
            marginBottom: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(0,229,255,0.2)', color: '#00e5ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Newspaper size={18} />
              </div>
              <div>
                <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#00e5ff', fontWeight: 600, letterSpacing: '0.5px' }}>Resume Blog Post</span>
                <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#ffffff' }}>{lastRead.title}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button 
                onClick={() => openBlogPost(lastRead)}
                className="editorial-read-btn"
                style={{ padding: '6px 14px' }}
              >
                Resume <ChevronRight size={14} />
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('last_read_blog');
                  setLastRead(null);
                }}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                title="Dismiss"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        <div className="editorial-layout-grid">
          
          {/* Left Main Column: Editorial Blog Feed List (~70%) */}
          <main className="editorial-feed-column">
            
            {/* Loading Indicator */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div className="loader" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: '#94a3b8' }}>Fetching blog posts from server...</p>
              </div>
            )}

            {/* Error Banner */}
            {!loading && error && (
              <div style={{ textAlign: 'center', padding: '40px 20px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '16px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <AlertCircle size={36} color="#ef4444" style={{ marginBottom: '12px' }} />
                <h3 style={{ color: '#f8fafc', marginBottom: '6px' }}>Unable to load blog posts</h3>
                <p style={{ color: '#fca5a5', fontSize: '0.9rem' }}>{error}</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && sortedPosts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <Newspaper size={48} color="#64748b" style={{ marginBottom: '16px' }} />
                <h3 style={{ color: '#f8fafc', marginBottom: '8px' }}>No blog posts found</h3>
                <p style={{ color: '#94a3b8' }}>No blog entries match your search or category filter.</p>
              </div>
            ) : !loading && !error && (
              currentPosts.map((post) => (
                <article 
                  key={post.id} 
                  className="editorial-list-card"
                  onClick={() => openBlogPost(post)}
                  style={{ cursor: 'pointer' }}
                >
                  
                  {/* Left Cover Image Thumbnail / Fallback Badge */}
                  <div className="editorial-thumb-wrapper">
                    {post.coverImageUrl ? (
                      <img src={post.coverImageUrl} alt={post.title} className="editorial-thumb-img" />
                    ) : (
                      <div className="editorial-thumb-fallback">
                        <Newspaper size={28} />
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.9 }}>
                          {post.category || 'Blog Post'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Right Content & Meta Details */}
                  <div className="editorial-card-content">
                    <div>
                      {/* Category Chip & Time Metadata */}
                      <div className="editorial-card-meta-top">
                        <span className={`editorial-cat-chip ${getCatClass(post.category)}`}>
                          {post.category || 'Blog'}
                        </span>
                        <span className="editorial-meta-item">
                          <Clock size={13} /> {post.readingTimeMinutes || 6} min read
                        </span>
                        {post.publishedAt && (
                          <span className="editorial-meta-item">
                            <Calendar size={13} /> {new Date(post.publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </div>

                      {/* Title & Excerpt */}
                      <h2 className="editorial-card-title">
                        {post.title}
                      </h2>
                      <p className="editorial-card-excerpt">
                        {post.summary || post.subtitle || 'Click to read full blog entry.'}
                      </p>
                    </div>
                  </div>

                </article>
              ))
            )}

            {/* Pagination Controls */}
            {!loading && !error && totalPages > 1 && (
              <div className="editorial-pagination-bar">
                <button
                  className="pagination-btn"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} /> Previous
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`page-num-dot ${currentPage === i + 1 ? 'active' : ''}`}
                      onClick={() => goToPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  className="pagination-btn"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}

          </main>

          {/* Right Sticky Sidebar Pane: Google AdSense (~30%) */}
          <aside className="editorial-sidebar-pane">
            
            {/* Google AdSense Display Banner Widget 1 */}
            <div className="adsense-sidebar-widget">
              <span className="adsense-label">Advertisement</span>
              <AdBanner type="square" />
            </div>

            {/* Google AdSense Skyscraper Banner Widget 2 */}
            <div className="adsense-sidebar-widget">
              <span className="adsense-label">Sponsor</span>
              <AdBanner type="skyscraper" />
            </div>

          </aside>

        </div>
      </div>
    </div>
  );
};

export default Blog;